<script setup>
import { ref, onMounted, nextTick } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import axios from 'axios';

const props = defineProps({
    groupedGalleries: Object,
    categories: {
        type: Array,
        default: () => ['Rental Mobil', 'Open Trip', 'Wisata', 'Lainnya']
    }
});

// State
const galleries = ref([]);
const loading = ref(true);
const saving = ref(false);
const isEditing = ref(false);
const originalData = ref(null);
const modalType = ref('error');
const errorModalContent = ref([]);
const previewImage = ref(null);
const togglingId = ref(null);

// Delete state
const deleteId = ref(null);
const deleteName = ref('');
const deleteConfirmedCheck = ref(false);
const deleting = ref(false);

const form = ref({
    id: null,
    title: '',
    description: '',
    category: '',
    image_alt: '',
    date_taken: '',
    is_active: true,
    image: null
});

// Categories for dropdown
const categoryOptions = ref([]);
const newCategory = ref('');
const addingCategory = ref(false);

// Methods
const loadGalleries = async (silent = false) => {
    if (!silent) loading.value = true;
    try {
        const response = await axios.get('/admin/gallery/get');
        if (response.data.status === 'success') {
            galleries.value = response.data.data;
        }
    } catch (error) {
        // Fallback to props
        if (props.groupedGalleries?.all) {
            galleries.value = props.groupedGalleries.all;
        }
    } finally {
        loading.value = false;
    }
};

const loadCategories = async () => {
    try {
        const response = await axios.get('/admin/gallery/categories');
        if (response.data.status === 'success') {
            categoryOptions.value = response.data.data.map(c => c.name);
        }
    } catch (error) {
        // Fallback to props
        if (props.categories?.length > 0) {
            categoryOptions.value = props.categories;
        } else {
            categoryOptions.value = ['Rental Mobil', 'Open Trip', 'Wisata', 'Lainnya'];
        }
    }
};

const addCategory = async () => {
    if (!newCategory.value?.trim()) {
        showModal('error', 'Nama kategori wajib diisi');
        return;
    }
    
    addingCategory.value = true;
    try {
        const response = await axios.post('/admin/gallery/categories', {
            name: newCategory.value.trim()
        });
        if (response.data.status === 'success') {
            categoryOptions.value.push(response.data.data.name);
            form.value.category = response.data.data.name;
            newCategory.value = '';
        }
    } catch (error) {
        showModal('error', error.response?.data?.message || 'Gagal menambahkan kategori');
    } finally {
        addingCategory.value = false;
    }
};

const showModal = (type, messages) => {
    modalType.value = type;
    errorModalContent.value = Array.isArray(messages) ? messages : [messages];
    nextTick(() => {
        try {
            const modalEl = document.getElementById('notificationModal');
            if (modalEl && typeof bootstrap !== 'undefined') {
                const modal = new bootstrap.Modal(modalEl);
                modal.show();
            }
        } catch (e) {
            alert(errorModalContent.value.join('\n'));
        }
    });
};

const resetForm = () => {
    form.value = {
        id: null,
        title: '',
        description: '',
        category: '',
        image_alt: '',
        date_taken: '',
        is_active: true,
        image: null
    };
    isEditing.value = false;
    originalData.value = null;
    previewImage.value = null;
    
    // Reset file input
    const fileInput = document.getElementById('gallery_image');
    if (fileInput) fileInput.value = '';
};

const isDataChanged = () => {
    if (!originalData.value) return true;
    
    // Compare fields
    const formData = form.value;
    const orig = originalData.value;
    
    return (
        formData.title !== orig.title ||
        formData.description !== orig.description ||
        formData.category !== orig.category ||
        formData.image_alt !== orig.image_alt ||
        formData.date_taken !== orig.date_taken ||
        formData.image !== null // New image uploaded
    );
};

const editGallery = (gallery) => {
    form.value = {
        id: gallery.id,
        title: gallery.title || '',
        description: gallery.description || '',
        category: gallery.category || '',
        image_alt: gallery.image_alt || '',
        date_taken: gallery.date_taken || '',
        is_active: gallery.is_active,
        image: null
    };
    
    originalData.value = { ...form.value };
    isEditing.value = true;
    previewImage.value = '/storage/gallery/' + gallery.image_path;
};

const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
        form.value.image = file;
        previewImage.value = URL.createObjectURL(file);
    }
};

const submitForm = async () => {
    saving.value = true;
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Validation
    const errors = [];
    if (!form.value.title?.trim()) errors.push('Judul foto wajib diisi');
    if (!form.value.category?.trim()) errors.push('Kategori layanan wajib dipilih');
    if (!isEditing.value && !form.value.image) errors.push('Gambar wajib diupload');
    
    if (errors.length > 0) {
        saving.value = false;
        showModal('error', errors);
        return;
    }
    
    // Check for changes in edit mode
    if (isEditing.value && !isDataChanged()) {
        saving.value = false;
        showModal('warning', 'Tidak ada perubahan data yang disimpan.');
        return;
    }
    
    const formData = new FormData();
    formData.append('title', form.value.title);
    formData.append('description', form.value.description || '');
    formData.append('category', form.value.category);
    formData.append('image_alt', form.value.image_alt || '');
    formData.append('date_taken', form.value.date_taken || '');
    formData.append('is_active', form.value.is_active ? 1 : 0);
    
    if (form.value.image) {
        formData.append('image', form.value.image);
    }
    
    let url = '/admin/gallery';
    if (isEditing.value) {
        formData.append('_method', 'PUT');
        url = `/admin/gallery/${form.value.id}`;
    }
    
    try {
        const response = await axios.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data.status === 'success') {
            resetForm();
            resetForm();
            loadGalleries(true);
        }
    } catch (error) {
        showModal('error', error.response?.data?.message || 'Terjadi kesalahan');
    } finally {
        saving.value = false;
    }
};

// Delete Methods
const confirmDelete = (gallery) => {
    deleteId.value = gallery.id;
    deleteName.value = gallery.title;
    deleteConfirmedCheck.value = false;
    const modal = new bootstrap.Modal(document.getElementById('deleteModal'));
    modal.show();
};

const submitDelete = async () => {
    if (!deleteId.value || !deleteConfirmedCheck.value) return;
    
    deleting.value = true;
    try {
        await axios.delete(`/admin/gallery/${deleteId.value}`);
        
        // Close modal
        const modalEl = document.getElementById('deleteModal');
        const modal = bootstrap.Modal.getInstance(modalEl);
        if (modal) modal.hide();
        
        // Reload galleries
        loadGalleries(true);
    } catch (error) {
        showModal('error', error.response?.data?.message || 'Gagal menghapus foto');
    } finally {
        deleting.value = false;
    }
};

const toggleStatus = async (gallery) => {
    togglingId.value = gallery.id;
    try {
        await axios.post('/admin/gallery/toggle-status', {
            id: gallery.id,
            is_active: !gallery.is_active
        });
        await loadGalleries(true);
    } catch (error) {
        showModal('error', 'Gagal mengubah status');
    } finally {
        togglingId.value = null;
    }
};

onMounted(() => {
    loadCategories();
    loadGalleries();
});
</script>

<template>
    <Head title="Kelola Gallery" />

    <div class="container-full">
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-block">
                    <h4 class="page-title">Kelola Gallery</h4>
                    <div class="d-inline-block align-items-center">
                        <nav>
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item">
                                    <Link :href="route('dashboard.index')"><i class="mdi mdi-home-outline"></i></Link>
                                </li>
                                <li class="breadcrumb-item active" aria-current="page">Gallery</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="row">
                <!-- Form Panel -->
                <div class="col-xl-4 col-12">
                    <div class="card">
                        <form @submit.prevent="submitForm">
                            <div class="card-header">
                                <h4 class="card-title">
                                    <i class="ti-image me-5"></i>{{ isEditing ? 'Edit' : 'Tambah' }} Foto
                                </h4>
                            </div>
                            <div class="card-body">
                                <div class="form-group mb-3">
                                    <label for="gallery_title" class="form-label">Judul <span class="text-danger">*</span></label>
                                    <input id="gallery_title" name="gallery_title" type="text" v-model="form.title" class="form-control">
                                </div>
                                
                                <div class="form-group mb-3">
                                    <label for="gallery_category" class="form-label">Kategori Layanan <span class="text-danger">*</span></label>
                                    <select id="gallery_category" name="gallery_category" v-model="form.category" class="form-select">
                                        <option value="">-- Pilih Kategori --</option>
                                        <option v-for="cat in categoryOptions" :key="cat" :value="cat">{{ cat }}</option>
                                    </select>
                                    <div class="input-group mt-2">
                                        <input type="text" id="new_category" name="new_category" v-model="newCategory" class="form-control form-control-sm" placeholder="Kategori baru...">
                                        <button type="button" class="btn btn-outline-success btn-sm" @click="addCategory" :disabled="addingCategory">
                                            <span v-if="addingCategory" class="spinner-border spinner-border-sm"></span>
                                            <i v-else class="ti-plus"></i>
                                        </button>
                                    </div>
                                    <small class="text-muted">Ketik dan klik + untuk menambah kategori baru</small>
                                </div>
                                
                                <div class="form-group mb-3">
                                    <label for="gallery_description" class="form-label">Deskripsi</label>
                                    <textarea id="gallery_description" name="gallery_description" v-model="form.description" class="form-control" rows="3"></textarea>
                                </div>
                                
                                <div class="form-group mb-3">
                                    <label for="gallery_image" class="form-label">Gambar {{ isEditing ? '' : '*' }}</label>
                                    
                                    <div class="image-preview-box mb-2">
                                        <div class="preview-content">
                                            <img v-if="previewImage" :src="previewImage" class="preview-img">
                                            <div class="placeholder-content" v-else>
                                                <i class="ti-image mb-2 fs-30 text-muted"></i>
                                                <div class="text-muted small lh-1">
                                                    <div>No Image</div>
                                                    <div class="xs-text text-muted mt-1">Min: 800x600</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <input id="gallery_image" name="gallery_image" type="file" class="form-control form-control-sm" @change="handleFileChange" accept="image/*">
                                    <div v-if="!previewImage && isEditing" class="metadata-info mt-1 text-muted small">
                                        *Biarkan kosong jika tidak ingin mengubah gambar
                                    </div>
                                </div>
                                
                                <div class="form-group mb-3">
                                    <label for="gallery_alt" class="form-label">Alt Text</label>
                                    <input id="gallery_alt" name="gallery_alt" type="text" v-model="form.image_alt" class="form-control">
                                </div>
                                
                                <div class="form-group mb-3">
                                    <label for="gallery_date" class="form-label">Tanggal Diambil</label>
                                    <input id="gallery_date" name="gallery_date" type="date" v-model="form.date_taken" class="form-control">
                                </div>
                            </div>
                            <div class="box-footer text-end">
                                <button type="button" v-if="isEditing" @click="resetForm" class="btn btn-warning me-2">
                                    <i class="ti-trash me-1"></i> Batal
                                </button>
                                <button type="submit" class="btn btn-primary" :disabled="saving">
                                    <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                                    <i v-else class="ti-save-alt me-1"></i> Simpan
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                
                <!-- Gallery Grid Panel -->
                <div class="col-xl-8 col-12">
                    <div class="card">
                        <div class="card-header">
                            <h4 class="card-title"><i class="ti-gallery me-10"></i>Daftar Foto</h4>
                        </div>
                        <div class="card-body">
                            <div v-if="loading" class="text-center p-5">
                                <div class="spinner-border text-primary"></div>
                            </div>
                            <div v-else-if="galleries.length === 0" class="text-center text-muted p-5">
                                <i class="ti-image fs-1 mb-3 d-block"></i>
                                Belum ada foto di gallery
                            </div>
                            <div v-else class="row">
                                <div v-for="gallery in galleries" :key="gallery.id" class="col-lg-4 col-md-6 mb-4">
                                    <div class="card border shadow-sm">
                                        <img :src="'/storage/gallery/' + gallery.image_path" 
                                            class="card-img-top" :alt="gallery.image_alt || gallery.title"
                                            style="height: 180px; object-fit: cover;">
                                        <div class="card-body p-3">
                                            <h6 class="card-title mb-2">{{ gallery.title }}</h6>
                                            <span class="badge badge-primary-light mb-2">{{ gallery.category || 'Tanpa Kategori' }}</span>
                                            <div class="d-flex justify-content-between align-items-center">
                                                <button 
                                                    class="btn btn-sm rounded-1 px-3" 
                                                    :class="gallery.is_active ? 'btn-success' : 'btn-danger'"
                                                    @click="toggleStatus(gallery)" 
                                                    :disabled="togglingId === gallery.id"
                                                    title="Klik untuk mengubah status"
                                                >
                                                    <span v-if="togglingId === gallery.id" class="spinner-border spinner-border-sm me-1"></span>
                                                    <i v-else :class="['fa me-1', gallery.is_active ? 'fa-check-circle' : 'fa-times-circle']"></i>
                                                    {{ gallery.is_active ? 'Aktif' : 'Nonaktif' }}
                                                </button>
                                                <div class="btn-group">
                                                    <button class="btn btn-outline-primary btn-sm" @click="editGallery(gallery)" title="Edit">
                                                        <i class="ti-pencil"></i>
                                                    </button>
                                                    <button class="btn btn-outline-danger btn-sm" @click="confirmDelete(gallery)" title="Hapus">
                                                        <i class="ti-trash"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Notification Modal -->
        <div class="modal fade" id="notificationModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header text-white" 
                        :class="{
                            'bg-danger': modalType === 'error',
                            'bg-warning text-dark': modalType === 'warning',
                            'bg-success': modalType === 'success',
                            'bg-info': modalType === 'info'
                        }">
                        <h5 class="modal-title">
                            <i class="me-2" :class="{
                                'ti-alert': modalType === 'error',
                                'fa fa-exclamation-triangle': modalType === 'warning',
                                'fa fa-check-circle': modalType === 'success',
                                'fa fa-info-circle': modalType === 'info'
                            }"></i>
                            {{ modalType === 'error' ? 'Peringatan Validasi' : modalType === 'warning' ? 'Peringatan' : modalType === 'success' ? 'Berhasil' : 'Informasi' }}
                        </h5>
                        <button type="button" class="btn-close" :class="modalType === 'warning' ? '' : 'btn-close-white'" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p class="mb-3" v-if="modalType === 'error'">Mohon perbaiki kesalahan berikut:</p>
                        <ul class="list-group list-group-flush">
                            <li class="list-group-item" 
                                :class="{
                                    'list-group-item-danger': modalType === 'error',
                                    'list-group-item-warning': modalType === 'warning',
                                    'list-group-item-success': modalType === 'success',
                                    'list-group-item-info': modalType === 'info'
                                }" 
                                v-for="(msg, index) in errorModalContent" :key="index">
                                <i class="fa me-2" :class="{
                                    'fa-exclamation-circle': modalType === 'error',
                                    'fa-exclamation-triangle': modalType === 'warning',
                                    'fa-check-circle': modalType === 'success',
                                    'fa-info-circle': modalType === 'info'
                                }"></i>{{ msg }}
                            </li>
                        </ul>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn" 
                            :class="{
                                'btn-danger': modalType === 'error',
                                'btn-warning': modalType === 'warning',
                                'btn-success': modalType === 'success',
                                'btn-info': modalType === 'info'
                            }" 
                            data-bs-dismiss="modal">
                            <i class="fa fa-check-square-o me-2"></i>Mengerti
                        </button>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Delete Confirmation Modal -->
        <div class="modal fade" id="deleteModal" tabindex="-1" aria-labelledby="deleteModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header bg-danger text-white">
                        <h5 class="modal-title" id="deleteModalLabel"><i class="fa fa-trash me-2"></i>Konfirmasi Hapus</h5>
                        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <p>Apakah Anda yakin ingin menghapus foto:</p>
                        <p class="fw-bold text-primary">"{{ deleteName }}"</p>
                        <p class="text-danger small mb-3">Tindakan ini tidak dapat dibatalkan.</p>
                        
                        <div class="form-check list-group-item list-group-item-danger p-3 rounded mb-0">
                            <input class="form-check-input" type="checkbox" id="confirmDeleteCheck" v-model="deleteConfirmedCheck">
                            <label class="form-check-label text-dark" for="confirmDeleteCheck">
                                Saya mengerti dan yakin ingin menghapus data ini.
                            </label>
                        </div>
                    </div>
                    <div class="modal-footer text-end">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><i class="fa fa-times me-2"></i>Batal</button>
                        <button type="button" class="btn btn-danger" :disabled="!deleteConfirmedCheck || deleting" @click="submitDelete">
                            <span v-if="deleting" class="spinner-border spinner-border-sm me-1"></span>
                            <i v-else class="fa fa-trash me-2"></i>Ya, Hapus
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Image Preview Styles */
.image-preview-box {
    position: relative;
    width: 100%;
    border: 1px solid #e4e6ef;
    border-radius: 0.45rem;
    background-color: #f5f8fa;
    overflow: hidden;
}

.preview-content {
    position: relative;
    width: 100%;
    height: 0;
    padding-bottom: 75%; /* 4:3 Aspect Ratio */
}

.preview-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.placeholder-content {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.fs-30 { font-size: 30px; }
</style>
